/**
 * Mentors service — Firestore CRUD for the `mentors` collection.
 *
 * A mentor document:
 *   {
 *     name, email, subjects: string[], timezone,
 *     teamsLink,                // permanent Microsoft Teams meeting link
 *     hourlyRate,               // payout rate (₹/hour) snapshotted onto session logs
 *     createdAt, updatedAt      // server timestamps
 *   }
 */
import firestoreService, { orderBy, where, limit } from './firestore.service';

const MENTORS = 'mentors';

function sanitize(input = {}) {
  const rate = Number(input.hourlyRate);
  return {
    name: (input.name || '').trim(),
    email: (input.email || '').trim().toLowerCase(),
    subjects: Array.isArray(input.subjects) ? input.subjects.filter(Boolean) : [],
    timezone: input.timezone || '',
    teamsLink: (input.teamsLink || '').trim(),
    hourlyRate: Number.isFinite(rate) && rate >= 0 ? rate : 0,
  };
}

export const mentorsService = {
  subscribe(onData, onError) {
    return firestoreService.subscribeCollection(
      MENTORS,
      [orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },

  list() {
    return firestoreService.getCollection(MENTORS, [orderBy('createdAt', 'desc')]);
  },

  /** Server-side total count (aggregation — no document downloads). */
  count() {
    return firestoreService.count(MENTORS);
  },

  get(id) {
    return firestoreService.getDocument(MENTORS, id);
  },

  /**
   * Resolve the mentor record for a signed-in mentor user by their email — the
   * link between an auth account (`users/{uid}`) and the admin-curated mentor
   * profile. Returns the matching record or null if the admin hasn't onboarded
   * this mentor yet. Emails are stored lowercased, so callers should lowercase.
   */
  async getByEmail(email) {
    if (!email) return null;
    const matches = await firestoreService.getCollection(MENTORS, [
      where('email', '==', email.trim().toLowerCase()),
      limit(1),
    ]);
    return matches[0] || null;
  },

  create(data) {
    return firestoreService.addDocument(MENTORS, sanitize(data));
  },

  update(id, data) {
    return firestoreService.updateDocument(MENTORS, id, sanitize(data));
  },

  remove(id) {
    return firestoreService.deleteDocument(MENTORS, id);
  },
};

export default mentorsService;
